"use client";

/**
 * Timeline Column Component
 * Renders the complete timeline with dots and line segments
 *
 * ARCHITECTURE:
 * This component orchestrates TimelineDot and TimelineLineSegment components
 * to create a continuous vertical timeline on the left side of the itinerary.
 *
 * DESIGN SPECS:
 * - Fixed width: 8px (width of the dots)
 * - Height: 100% (matches content column height)
 * - Position: relative (so children can position absolutely)
 * - Responsive: Hidden on mobile (xs/sm), visible on desktop (md+)
 *   Use: sx={{ display: { xs: "none", md: "block" } }}
 *
 * TIMELINE ITEMS:
 * Each item represents a section (optional or main tour day):
 * {
 *   type: "optional" | "main",
 *   hasBullet: boolean,      // Gets a dot/bullet if true
 *   ref: React.RefObject,    // To measure content height
 * }
 *
 * DOT POSITIONING LOGIC:
 * Uses getBoundingClientRect() to measure ACTUAL DOM positions, accounting for:
 * - Stack gap spacing (~38px between elements)
 * - Margins and padding
 * - Dynamic content sizing
 *
 * - Optional sections: Dot centered on actual element
 * - Main tour days: Dot positioned 37px from actual top of element
 *
 * LINE SEGMENT LOGIC:
 * - Between dots, render TimelineLineSegment
 * - variant="dotted" for optional sections
 * - variant="solid" for main tour sections
 * - Calculate startY and height based on actual measured positions
 *
 * POSITIONING APPROACH:
 * 1. Get container's top position using getBoundingClientRect()
 * 2. For each content item, get its bounding rect
 * 3. Calculate relative position: item.top - container.top
 * 4. Use relative positions for dot and line placement
 *
 * USAGE:
 * <TimelineColumn
 *   items={[
 *     { type: "optional", hasBullet: true, ref: preNightRef },
 *     { type: "main", hasBullet: true, ref: day1Ref },
 *     { type: "main", hasBullet: false, ref: day2Ref }
 *   ]}
 *   testIdPrefix="pdp"
 * />
 */

import React from "react";
import { Box } from "@mui/material";
import TimelineDot from "./timeline-dot";
import TimelineLineSegment from "./timeline-line-segment";
import { useTimelineMeasurements } from "./use-timeline-measurements";
import { TIMELINE_DAY_DOT_OFFSET } from "./timeline-constants";

export type { TimelineItem } from "./use-timeline-measurements";

export interface TimelineColumnProps {
  items: import("./use-timeline-measurements").TimelineItem[];
  nodes: (HTMLDivElement | null)[]; // DOM nodes from callback refs
  testIdPrefix: string;
}

const TimelineColumn: React.FC<TimelineColumnProps> = ({
  items,
  nodes,
  testIdPrefix
}) => {
  // Use custom hook for all measurement logic
  const { positions, containerRef } = useTimelineMeasurements(items, nodes);

  // STEP 1: Build array of dots with their positions and item metadata
  interface Dot {
    position: number;
    itemIndex: number;
    itemType: "optional" | "main";
  }

  const dots: Dot[] = [];

  items.forEach((item, index) => {
    if (item.hasBullet && positions[index]) {
      // Calculate absolute position for this dot using actual measurements
      const dotPosition = item.type === "optional"
        ? positions[index].center  // Centered on actual element
        : positions[index].top + TIMELINE_DAY_DOT_OFFSET; // Offset from actual top

      dots.push({
        position: dotPosition,
        itemIndex: index,
        itemType: item.type
      });
    }
  });

  // STEP 2: Build line segments BETWEEN consecutive dots
  interface LineSegment {
    startY: number;
    height: number;
    variant: "solid" | "dotted";
  }

  const lineSegments: LineSegment[] = [];

  dots.forEach((currentDot, index) => {
    // Only create line if there's a next dot
    if (index < dots.length - 1) {
      const nextDot = dots[index + 1];

      // Line variant determined by SOURCE dot (the current dot)
      // SOLID if source is main tour day (represents main tour flow)
      // DOTTED if source is optional section (represents optional flow)
      const variant = currentDot.itemType === "main" ? "solid" : "dotted";

      lineSegments.push({
        startY: currentDot.position,
        height: nextDot.position - currentDot.position,
        variant
      });
    } else if (currentDot.itemType === "main") {
      // For the last dot, ONLY extend line if it's a main tour day (collapsed state)
      // When expanded, last dot is optional (post-night), so no extension
      const lastItemIndex = currentDot.itemIndex;
      const lastItemPosition = positions[lastItemIndex];
      if (lastItemPosition) {
        const variant = "solid";
        const bottomPosition = lastItemPosition.top + lastItemPosition.height;
        lineSegments.push({
          startY: currentDot.position,
          height: bottomPosition - currentDot.position,
          variant
        });
      }
    }
  });

  // STEP 3: Build render items from dots and line segments
  type RenderItem = {
    type: "dot" | "line";
    key: string;
    dotPosition?: number; // Absolute Y position in pixels
    lineVariant?: "solid" | "dotted";
    startY?: number;
    height?: number;
  };

  const renderItems: RenderItem[] = [];

  // Add all dots to render items
  dots.forEach((dot, index) => {
    renderItems.push({
      type: "dot",
      key: `dot-${index}`,
      dotPosition: dot.position
    });
  });

  // Add all line segments to render items
  lineSegments.forEach((segment, index) => {
    renderItems.push({
      type: "line",
      key: `line-${index}`,
      lineVariant: segment.variant,
      startY: segment.startY,
      height: segment.height
    });
  });

  return (
    <Box
      ref={containerRef}
      data-testid={`${testIdPrefix}_timeline-column`}
      sx={{
        width: "8px",
        height: "100%",
        position: "relative",
        flexShrink: 0,
        display: { xs: "none", legacyTablet: "none", legacyDesktop: "block" } // Hidden on mobile/tablet, visible on desktop
      }}
    >
      {renderItems.map((renderItem) => {
        if (renderItem.type === "dot") {
          return (
            <TimelineDot
              key={renderItem.key}
              position={renderItem.dotPosition!}
              testIdPrefix={testIdPrefix}
            />
          );
        }
        return (
          <TimelineLineSegment
            key={renderItem.key}
            variant={renderItem.lineVariant!}
            startY={renderItem.startY!}
            segmentHeight={renderItem.height!}
            testIdPrefix={testIdPrefix}
          />
        );
      })}
    </Box>
  );
};

export default TimelineColumn;
